<template>
  <v-card>
    <v-card-title>
      Allocation Form
    </v-card-title>
    <v-card-text>
      <div v-if="allocation">
        <v-row>
          <v-col v-if="allocation.job" cols="6" class="text-left text-subtitle-1">
            {{ allocation.job.code }} - {{ allocation.job.project.name }}; {{ allocation.job.reference }}
          </v-col>

          <v-col v-if="allocation.status_id in statusMap" cols="6" class="text-right">
            <v-chip :color="statusMap[allocation.status_id].color" :text-color="statusMap[allocation.status_id].text_color">{{ statusMap[allocation.status_id].name }}</v-chip>
          </v-col>
        </v-row>

        <v-row class="text-subtitle-2 mt-0">
          <v-col v-if="allocation.user">
            Created By: {{ allocation.user.first_name }} {{ allocation.user.last_name }}
          </v-col>
        </v-row>

        <v-row class="text-subtitle-2 mt-0">
          <v-col>
            Created At: {{ allocation.created_at }}
          </v-col>
        </v-row>

        <v-row class="mt-0">
          <v-col cols="3">
            <v-menu ref="menuAllocateBy" v-model="showMenuAllocateBy" transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="allocation.allocate_by"
                  @change="$refs.menuAllocateBy.save(allocation.allocate_by)"
                  v-on="on"
                  v-bind="attrs"
                  readonly
                  hide-details
                  append-icon="mdi-calendar"
                >
                  <template #label>Allocate By<red-asterisk /></template>
                </v-text-field>
              </template>
              <v-date-picker v-model="allocation.allocate_by" no-title scrollable />
            </v-menu>
          </v-col>
          <v-col cols="3">
            <v-menu ref="menuSupplyBy" v-model="showMenuSupplyBy" transition="scale-transition" offset-y min-width="auto">
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="allocation.supply_by"
                  @change="$refs.menuSupplyBy.save(allocation.supply_by)"
                  v-on="on"
                  v-bind="attrs"
                  readonly
                  hide-details
                  append-icon="mdi-calendar"
                >
                  <template #label>Supply By</template>
                </v-text-field>
              </template>
              <v-date-picker v-model="allocation.supply_by" no-title scrollable />
            </v-menu>
          </v-col>
          <v-col class="text-right">
            <v-btn v-if="!showItemForm" color="primary" outlined @click="openItemForm">
              <v-icon small class="mr-1">
                fas fa-plus
              </v-icon>
              New Item
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <div v-if="showItemForm">
        <v-row>
          <v-col cols="4">
            <v-autocomplete v-model="newItem.inventory_item_id" :items="inventoryItems" item-value="id" item-text="name" label="Inventory Item" hide-details class="pt-0"/>
          </v-col>
          <v-col cols="3">
            <v-text-field v-model="newItem.qty_required" label="Quantity Required" hide-details class="pt-0"/>
          </v-col>
          <v-col cols="5" class="text-right">
            <v-btn @click="closeItemForm" color="info" outlined class="mr-2">CLOSE</v-btn>
            <v-btn @click="addItem" color="success" outlined>ADD</v-btn>
          </v-col>
        </v-row>
      </div>

      <v-row>
        <v-col cols="6" class="text-h5">BOM</v-col>
        <v-col cols="6" class="text-h5 text-right">Allocation</v-col>
      </v-row>


      <v-row class="mt-0">
        <v-col cols="12">
          <v-data-table
            v-if="isInitialized"
            :headers="allocationItemsHeaders"
            :items="allocation.items"
            hide-default-footer
            :items-per-page="-1"
            dense
          >
            <template #item="{ item }">
              <tr v-if="!item.is_deleted">
                <td class="text-center">{{ item.inventory_item.material_id || '' }}</td>
                <td>
                  <v-img 
                    v-if="extraDataMap[item.inventory_item_id].inventory_item_photo" 
                    @click="openItemShowImageForm(item)"
                    :src="`data:${extraDataMap[item.inventory_item_id].inventory_item_photo.photo_mime};base64, ${extraDataMap[item.inventory_item_id].inventory_item_photo.photo}`" 
                    style="width:50px"
                    />
                </td>
                <td class="text-center">{{ item.inventory_item.name || '' }}</td>
                <td class="text-center">{{ getArrayNames(item.inventory_item.colors, 'code') }}</td>
                <td class="text-center">{{ getArrayNames(item.inventory_item.uoms) }}</td>
                <td class="text-center">{{ getArrayNames(item.inventory_item.lengths) }}</td>
                <td class="text-center" style="border-right:2px solid black">{{ item.qty_required }}</td>
                <td class="text-center">{{ getAvailbleQty(item.inventory_item_id) }}/{{ getOrderedQty(item.inventory_item_id) }}</td>
                <td class="text-center">{{ extraDataMap[item.inventory_item_id].qty_allocated }}</td>
                <td class="text-center">{{ extraDataMap[item.inventory_item_id].qty_supplied }}</td>
                <td>
                  <div v-for="(tag, i) in item.tags" :key="`tag-${i}`">
                    <v-row v-if="!tag.is_deleted" class="my-0">
                      <v-col cols="6" class="pb-0">
                        <v-text-field v-if="tag.id" disabled dense :value="tag.inventory_tag" label="QR/UPC" />

                        <v-select v-else clearable dense 
                          v-model="tag.inventory_tag" 
                          :items="extraDataMap[item.inventory_item_id].available_tags" 
                          item-value="inventory_tag" 
                          item-text="inventory_tag" 
                          label="QR/UPC" 
                          hide-details
                          @change="onTagChanged(item, i)">
                          <template #item="{item}">
                            {{ item.inventory_tag }}, 
                            <span class="ml-1">{{ item.shelf }},</span>
                            <span class="ml-1" style="color: green">Qty:</span> {{ item.qty }}
                          </template>
                        </v-select>
                      </v-col>
                      <v-col cols="4" class="pb-0">
                        <v-text-field v-model.number="tag.qty" 
                          dense 
                          label="QTY" 
                          hide-details
                          type="number"
                          :min="0"
                          :error="!tagQtyIsValid(tag)"
                          @change="onTagQtyChanged(item)">

                        </v-text-field>
                      </v-col>
                      <v-col cols="2" class="pb-0">
                        <v-btn v-if="i !== item.tags.length - 1" color="error" icon @click="onClickRemoveQr(item, i)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                        <v-btn v-else color="success" icon @click="onClickAddQr(item)">
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </div>
                </td>
                <td>
                  <v-row class="my-0">
                    <v-col cols="8" class="pb-0">
                      <v-select 
                        clearable 
                        dense 
                        label="Inventory PO"
                        v-model="item.inventory_po_item_id"
                        :items="extraDataMap[item.inventory_item_id].available_po_items" 
                        item-value="id" 
                        item-text="name" 
                        hide-details
                        @change="adjustPoQty(item)"/>

                    </v-col>
                    <v-col cols="4" class="pb-0">
                      <v-text-field 
                        v-model.number="item.inventory_po_item_qty" 
                        dense 
                        label="QTY" 
                        hide-details
                        type="number" />
                    </v-col>
                  </v-row>
                </td>
                <td>
                  <v-menu offset-y>
                    <template #activator="{ on, attrs }">
                      <v-btn class="ml-1 my-1" elevation="0" fab outlined x-small v-bind="attrs" tile color="primary" style="border-radius: 7px; width: 28px !important; height: 28px !important;" v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list class="pa-3">
                      <v-list-item @click="deleteItem(item)">
                        <v-list-item-title :style="`color: red; font-weight: 600;`">
                          <v-row align="center">
                            <v-col cols="auto" class="pr-0" style="white-space: nowrap;">
                              <v-icon style="font-size: 16px;" color="error">fas fa-trash</v-icon>
                            </v-col>
                            <v-col class="pt-4 text-color-error">DELETE</v-col>
                          </v-row>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="text-h5">Extra Items</v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            v-if="isInitialized"
            :headers="extraItemsHeaders"
            :items="extraItems"
            hide-default-footer
            :items-per-page="-1"
            dense
          >
            <template #item="{ item }">
              <tr>
                <td class="text-center">{{ item.inventory_tag }}</td>
                <td class="text-center">{{ item.material_id }}</td>
                <td class="text-center">{{ item.inventory_name }}</td>
                <!-- <td class="text-center">{{ getArrayNames(item.inventory_item.uoms) }}</td>
                <td class="text-center">{{ getArrayNames(item.inventory_item.lengths) }}</td> -->
                <td class="text-center">{{ item.qty_oversupplied }}</td>
                <td class="text-center"></td>
                <td class="text-center"></td>
                <td class="text-center"></td>
              </tr>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <InventoryItemShowImageForm
        ref="itemShowImageForm"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn color="normal" class="mr-2" @click="close">
        CLOSE
      </v-btn>
      <v-btn color="primary" @click="submitAllocation">
        SUBMIT
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import * as allocationApi from '../../../api/InventoryAllocationApi';
import InventoryItemShowImageForm from '../InventoryItemShowImageForm.vue';
import RedAsterisk from '../../RedAsterisk.vue';

export default {
  components: {
    InventoryItemShowImageForm,
    RedAsterisk
  },
  emits: ['close'],
  data: () => ({
    isInitialized: false,

    allocationItemsHeaders: [
      { text: 'Item Code', align: 'center' },
      { text: 'Picture', align: 'center' },
      { text: 'Item Name', align: 'center' },
      { text: 'Colour', align: 'center' },
      { text: 'UOM', align: 'center' },
      { text: 'Length', align: 'center' },
      { text: 'Qty', align: 'center' },
      { text: 'Qty Available/Ordered', align: 'center' },
      { text: 'Qty Allocated', align: 'center' },
      { text: 'Qty Supplied', align: 'center' },
      { text: 'QR', align: 'center' },
      { text: 'Inventory PO', align: 'center' },
      { text: 'Actions', align: 'center' },
    ],
    allocation: null,
    extraDataMap: {},
    tagQtyRemainingMap: {},
    showMenuAllocateBy: false,
    showMenuSupplyBy: false,
    showItemForm: false,
    statusMap: {},
    newItem: {
      qty_required: null,
      inventory_item_id: null
    },
    inventoryItems: [],
    extraItemsHeaders: [
      { text: 'QR Code', align: 'center' },
      { text: 'Item Code', align: 'center' },
      { text: 'Item Name', align: 'center' },
      // { text: 'UOM', align: 'center' },
      // { text: 'Length', align: 'center' },
      { text: 'Qty Oversupplied', align: 'center' },
      { text: 'Reason', align: 'center' },
      { text: 'Note', align: 'center' },
      { text: 'Acknowledge', align: 'center' },
    ],
    extraItems: [],

  }),
  computed: {
    ...mapState({
      search: (state) => state.search,
      user: (state) => state.user,
    }),
    ...mapGetters([
      'businessRulesGetBoolean',
    ]),
  },
  watch: {

  },
  created() {
    Promise.all([
      this.$api.inventoryItem.readData(),
      allocationApi.getStatuses()
    ]).then(([inventoryItems, statuses]) => {
      this.inventoryItems = inventoryItems.data;
      this.statusMap = statuses.reduce((acc, x) => {
        acc[x.id] = x;
        return acc;
      }, {});
    })
    this.$api.inventoryItem.readData().then(res => {
      this.inventoryItems = res.data;
    });
  },
  methods: {
    async open(allocationId) {
      this.$bus.$emit('loading');
      this.resetForm();
      Promise.all([
        allocationApi.getById(allocationId),
        allocationApi.getAllocationData(allocationId)
      ]).then(([allocation, allocationData]) => {
        this.allocation = allocation;
        this.extraDataMap = allocationData.items;
        this.extraItems = allocationData.extra_items;
        console.log(allocationData)

        for (let inventoryItemId in this.extraDataMap) {
          //TODO tag qty remianing map
        }

        this.allocation.items.forEach(item => {
          if (!item.tags) item.tags = [{ id: null, inventory_tag: null, qty: null }];
          else item.tags.push({ id: null, inventory_tag: null, qty: null });
        });

        this.isInitialized = true;
        this.$bus.$emit('loading');
      });
    },
    resetForm() {
      this.allocation = null;
      this.extraDataMap = {};
      this.extraItems = [];
    },
    close() {
      this.$emit('close');
    },
    getArrayNames(array, key = 'name') {
      if (!array) return '';
      if (typeof(array) === 'string') return array;
      return array.map(x => x[key]).join(', ');
    },
    onClickAddQr(allocationItem) {
      allocationItem.tags.push({ id: null, inventory_tag: null, qty: null });
    },
    onClickRemoveQr(allocationItem, idx) {
      if (!allocationItem.tags[idx].id) {
        allocationItem.tags.splice(idx, 1);
      } else {
        allocationItem.tags[idx].is_deleted = 1;
      }
    },
    deleteItem(item) {
      if (item.id === null) {
        this.allocation.items = this.allocation.items.filter(x => x !== item);
      } else {
        item.is_deleted = 1;
      }
    },
    getPoQty(allocationItem) {
      const selectedPoItemId = allocationitem.inventory_po_item_id;
      if (!selectedPoItemId) return null;
      const selectedPoItem = this.extraDataMap[allocationItem.inventory_item_id].available_po_items.find(x => x.id === selectedPoItemId)
      const qtyAllocated = allocationItem.tags.reduce((acc, x) => acc += x.qty, 0);
      const qtyLeftToFill = allocationItem.qty_required - qtyAllocated - tagQtyEntered;
      return Math.min(qtyLeftToFill, selectedPoItem.qty);
    },
    adjustPoQty(allocationItem) {
      allocationItem.inventory_po_item_qty = this.getPoQty(allocationItem) || '';
    },
    getAvailbleQty(inventoryItemId) {
      if (!(inventoryItemId in this.extraDataMap)) return 0;
      return this.extraDataMap[inventoryItemId].available_tags.map(x => x.qty).reduce((acc, x) => acc += x, 0);
    },
    getOrderedQty(inventoryItemId) {
      if (!(inventoryItemId in this.extraDataMap)) return 0;
      return this.extraDataMap[inventoryItemId].available_po_items.map(x => x.qty).reduce((acc, x) => acc += x, 0);
    },
    itemIsFullyAllocated(allocationItem) {
      return this.extraDataMap[allocationItem.inventory_item_id].qty_allocated >= allocationItem.qty_required;
    },
    getMaxTagQuantity(allocationItem, tagIdx = null) {
      //TODO review this
      const requiredQty = allocationItem.qty_required;
      const qtyAllocated = allocationItem.tags.reduce((acc, x, i) => acc += i !== tagIdx ? x.qty : 0, 0);
      const qtyLeftToFill = requiredQty - qtyAllocated;
      const selectedTag = allocationItem.tags[tagIdx]?.inventory_tag ?? null;
      if (selectedTag === null) return qtyLeftToFill;
      const selectedTagQty = this.extraDataMap[allocationItem.inventory_item_id].available_tags.find(x => x.inventory_tag === selectedTag)?.qty || 0;
      return Math.min(selectedTagQty, qtyLeftToFill);
    },

    onTagQtyChanged(allocationItem) {
      this.adjustPoQty(allocationItem);
    },

    onTagChanged(allocationItem, tagIdx) {
      //TODO review this
      allocationItem.tags[tagIdx].max_qty = this.getMaxTagQuantity(allocationItem, tagIdx);
    },

    tagQtyIsValid(tag) {
      //TODO
      return true;
      // return tag.qty <= tag.max_qty && tag.qty >= 0;
    },

    openItemForm() {
      this.newItem.qty_required = null;
      this.newItem.inventory_item_id = null;
      this.showItemForm = true;
    },

    closeItemForm() {
      this.newItem.qty_required = null;
      this.newItem.inventory_item_id = null;
      this.showItemForm = false;
    },

    async addItem() {
      if (!this.newItem.inventory_item_id) return this.$bus.$emit('showError', 'Please select Inventory Item');
      if (!this.newItem.qty_required) return this.$bus.$emit('showError', 'Please enter Quantity Required');
      if (this.newItem.inventory_item_id in this.extraDataMap) return this.$bus.$emit('showError', 'This Inventory Item already exists in this Allocation');
  
      try {
        const inventoryItemId = this.newItem.inventory_item_id;
        const inventoryItemData = await allocationApi.getAllocationDataForInventoryItem(inventoryItemId);
        console.log(inventoryItemData)
        this.extraDataMap[inventoryItemId] = {
          ...inventoryItemData,
          qty_allocated: 0,
          qty_supplied: 0,
        };

        console.log(this.extraDataMap)
        
        this.allocation.items.push({
          id: null,
          inventory_item_id: inventoryItemId,
          inventory_item: this.inventoryItems.find(x => x.id === this.newItem.inventory_item_id),
          qty_required: this.newItem.qty_required,
          inventory_po_item_id: null,
          inventory_po_item_qty: null,
          tags: [{ id: null, inventory_tag: null, qty: null }],
        });

        this.closeItemForm();
      } catch (e) {
        console.error(e);
        return this.$bus.$emit('showError', 'Failed to add an item');
      }
    },

    openItemShowImageForm(allocationItem) {
      const photoData = this.extraDataMap[allocationItem.inventory_item_id].inventory_item_photo;
      if (!photoData) return;
      this.$refs.itemShowImageForm.openDialog(photoData.photo, photoData.mime_type, allocationItem.inventory_item.material_id);
    },

    async submitAllocation() {
      try {
        if (!this.allocation.allocate_by) return this.$bus.$emit('showError', 'Please enter the Allocate By date');
        //TODO validate quantities
        await allocationApi.updateAllocation(this.allocation);
        this.$bus.$emit('showSuccess', 'Allocation updated');
        this.close();
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', 'Failed to save the Allocation');
      }
    }
  },
};
</script>